/**
 * @file Tiny synchronous pub/sub. The engine emits, React subscribes (throttled
 * to ~10Hz in `useEngineBus`) — the engine must never import React.
 */

/**
 * @returns {import('../types.js').EventBus}
 */
export function createBus() {
  /** @type {Map<string, Set<Function>>} */
  const channels = new Map();

  /**
   * @param {string} type
   * @param {Function} fn
   * @returns {() => void} unsubscribe
   */
  function on(type, fn) {
    let set = channels.get(type);
    if (!set) channels.set(type, (set = new Set()));
    set.add(fn);
    return () => off(type, fn);
  }

  /**
   * @param {string} type
   * @param {Function} fn
   */
  function off(type, fn) {
    const set = channels.get(type);
    if (!set) return;
    set.delete(fn);
    if (set.size === 0) channels.delete(type);
  }

  /**
   * @param {string} type
   * @param {any} [payload]
   */
  function emit(type, payload) {
    const set = channels.get(type);
    if (!set) return;
    // Copy: handlers are allowed to unsubscribe themselves while emitting.
    for (const fn of [...set]) {
      try {
        fn(payload);
      } catch (err) {
        console.error(`[bus] handler for "${type}" threw`, err);
      }
    }
  }

  /**
   * @param {string} type
   * @param {Function} fn
   * @returns {() => void}
   */
  function once(type, fn) {
    const unsub = on(type, (payload) => {
      unsub();
      fn(payload);
    });
    return unsub;
  }

  /** @param {string} [type] Clears one channel, or all of them. */
  function clear(type) {
    if (type === undefined) channels.clear();
    else channels.delete(type);
  }

  return { on, off, emit, once, clear };
}
